import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Search, FileText, DollarSign } from 'lucide-react';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function Showroom() {
  const navigate = useNavigate();
  const [searchCode, setSearchCode] = useState('');
  const [designData, setDesignData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Master data popup states
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [masterDataDetails, setMasterDataDetails] = useState(null);
  const [masterDataLoading, setMasterDataLoading] = useState(false);
  const [masterDataCode, setMasterDataCode] = useState('');
  
  // Costing and Tech Pack states
  const [showCostingDialog, setShowCostingDialog] = useState(false);
  const [showTechPackPdf, setShowTechPackPdf] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authAction, setAuthAction] = useState(null); // 'costing' or 'techpack'


  // Authorization handlers
  const handleCostingClick = () => {
    setAuthAction('costing');
    setAuthDialogOpen(true);
  };
  
  const handleTechPackClick = () => {
    setAuthAction('techpack');
    setAuthDialogOpen(true);
  };
  
  const handleAuthSuccess = () => {
    if (authAction === 'costing') {
      setShowCostingDialog(true);
    } else if (authAction === 'techpack') {
      setShowTechPackPdf(true);
    }
    setAuthDialogOpen(false);
    setAuthAction(null);
  };

  // Enrich design data with live master data
  const enrichWithLiveData = async (design) => {
    try {
      const token = localStorage.getItem('token');
      const enrichedDesign = { ...design };

      // Fetch live fabric data
      if (design.fabrics && design.fabrics.length > 0) {
        const fabricsResponse = await axios.get(`${API}/api/fabric-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        enrichedDesign.fabrics = design.fabrics.map(fabric => {
          const masterFabric = fabricsResponse.data.find(f => f.ln_code === fabric.fabric_code);
          if (masterFabric) {
            const masterColor = masterFabric.colors?.find(c => c.color_code === fabric.color_code);
            if (masterColor) {
              return {
                ...fabric,
                color_name: masterColor.color_name,
                width: masterFabric.width,
                gsm: masterFabric.gsm,
                composition: masterFabric.composition,
                fabric_type: masterFabric.fabric_type,
                price_per_meter: masterColor.price_per_meter
              };
            }
          }
          return fabric;
        });
      }

      // Fetch live trim data
      if (design.trims && design.trims.length > 0) {
        const trimsResponse = await axios.get(`${API}/api/trims-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const packagingResponse = await axios.get(`${API}/api/packaging-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        enrichedDesign.trims = design.trims.map(trim => {
          // Check trims
          let masterTrim = trimsResponse.data.find(t => t.ln_z_code === trim.trim_code);
          if (!masterTrim) {
            // Check packaging
            masterTrim = packagingResponse.data.find(p => p.ln_y_code === trim.trim_code);
          }
          
          if (masterTrim) {
            const masterColor = masterTrim.colors?.find(c => c.color_code === trim.color_code);
            if (masterColor) {
              return {
                ...trim,
                color_name: masterColor.color_name,
                price: masterColor.price
              };
            }
          }
          return trim;
        });
      }

      return enrichedDesign;
    } catch (error) {
      console.error('Error enriching with live data:', error);
      return design; // Return original if enrichment fails
    }
  };

  // Fetch and show master data popup
  const handleMasterDataClick = async (code, type) => {
    setMasterDataLoading(true);
    setMasterDataOpen(true);
    setMasterDataCode(code);
    
    // Map frontend type to backend expected format
    const typeMap = {
      'Fabric': 'FABRIC LN',
      'Trim': 'TRIMS AND ACCESSORIES LN-Z',
      'Trims': 'TRIMS AND ACCESSORIES LN-Z',  // Handle both singular and plural
      'Packaging': 'PACKAGING LN-Y',
      'DigitalDesign': 'DIGITAL DESIGN LN-T'
    };
    
    const backendType = typeMap[type] || type;
    
    try {
      const token = localStorage.getItem('token');
      // Add timestamp to prevent browser caching and ensure live data fetch
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/api/master-data/by-code/${backendType}/${code}?_t=${timestamp}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setMasterDataDetails(response.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to fetch master data details');
      setMasterDataDetails(null);
    } finally {
      setMasterDataLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchCode.trim()) {
      toast.error('Please enter an LN-X code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/designs`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Find design by article_code matching the search code
      const design = response.data.find(d => d.article_code === searchCode.trim());

      if (design) {
        // Enrich with live data from master
        const enrichedDesign = await enrichWithLiveData(design);
        setDesignData(enrichedDesign);
        toast.success('Design found');
      } else {
        setDesignData(null);
        toast.error('No design found with this code');
      }
    } catch (error) {
      console.error('Error fetching design:', error);
      toast.error('Failed to fetch design data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchCode('');
    setDesignData(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Showroom
                </h1>
                <p className="text-sm text-gray-600 mt-1">Displayed products information</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-lg border-2 border-black p-6 mb-8">
          <form onSubmit={handleSearch} className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-sm font-semibold text-gray-500 mb-2">SEARCH BY LN-X CODE OR SCAN BARCODE</Label>
              <Input
                type="text"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                placeholder="Enter LN-X code"
                className="text-lg"
                autoFocus
              />
            </div>
            <Button 
              type="submit" 
              className="bg-black hover:bg-gray-800 h-10"
              disabled={loading}
            >
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            {designData && (
              <Button 
                type="button" 
                variant="outline"
                onClick={handleClear}
                className="h-10"
              >
                Clear
              </Button>
            )}
          </form>
        </div>

        {/* Design Data Display */}
        {designData ? (
          <div className="bg-white rounded-lg shadow-lg">
            <div className="space-y-1">
              {/* Basic Info with Barcode */}
              <div className="p-5 border-2 border-black">
                <div className="flex justify-between items-start">
                  <div className="grid grid-cols-2 gap-6 flex-1">
                    <div>
                      <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">ARTICLE CODE</p>
                      <p className="text-sm font-bold">{designData.article_code}</p>
                      
                      {/* Tech Pack and Costing Buttons */}
                      <div className="flex gap-2 mt-2">
                        {designData.tech_pack_pdf && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleTechPackClick}
                            className="border-2 border-black bg-white hover:bg-gray-100"
                          >
                            <FileText className="w-3 h-3 mr-1" /> Tech Pack
                          </Button>
                        )}
                        {designData.costing_approved && designData.costing_data && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleCostingClick}
                            className="border-2 border-black bg-white hover:bg-gray-100"
                          >
                            <DollarSign className="w-3 h-3 mr-1" /> Costing
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold tracking-wider text-gray-500 mb-1">CATEGORY</p>
                      <p className="text-sm font-bold">{designData.category || '-'}</p>
                    </div>
                  </div>
                  {/* Barcode in top right */}
                  {designData.article_barcode && (
                    <div className="ml-6 flex-shrink-0">
                      <img src={designData.article_barcode} alt="Article Barcode" style={{ maxWidth: '250px', height: 'auto' }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Fabrics */}
              {designData.fabrics && designData.fabrics.length > 0 && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">FABRICS</p>
                  <div className="space-y-3">
                    {designData.fabrics.map((fabric, index) => (
                      <div key={index} className="p-4 border border-gray-300 bg-gray-50">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                            <p 
                              className="text-sm font-bold underline cursor-pointer hover:text-blue-600"
                              onClick={() => handleMasterDataClick(fabric.fabric_code, 'Fabric')}
                            >
                              {fabric.fabric_code}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">COLOR</p>
                            <p className="text-sm">{fabric.color_code} - {fabric.color_name}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">CONSUMPTION</p>
                            <p className="text-sm">{fabric.consumption} {parseFloat(fabric.consumption) > 1 ? 'Meters' : 'Meter'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">COMPOSITION</p>
                            <p className="text-sm">{fabric.composition || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">WIDTH</p>
                            <p className="text-sm">{fabric.width}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">GSM</p>
                            <p className="text-sm">{fabric.gsm}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Digital Design */}
              {designData.digital_design_code && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">DIGITAL DESIGN</p>
                  <div className="p-4 border border-gray-300 bg-gray-50">
                    <div className="grid grid-cols-3 gap-6">
                      {/* Left side - Text info (2/3 width) */}
                      <div className="col-span-2 grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">CODE</p>
                          <p 
                            className="text-sm font-bold underline cursor-pointer hover:text-blue-600"
                            onClick={() => handleMasterDataClick(designData.digital_design_code, 'DigitalDesign')}
                          >
                            {designData.digital_design_code}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">COLORWAY</p>
                          <p className="text-sm">{designData.digital_design_colorway || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">TYPE</p>
                          <p className="text-sm">{designData.digital_design_type || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1">CONSUMPTION</p>
                          <p className="text-sm">{designData.digital_design_consumption ? `${designData.digital_design_consumption} ${parseFloat(designData.digital_design_consumption) > 1 ? 'Meters' : 'Meter'}` : '-'}</p>
                        </div>
                      </div>
                      
                      {/* Right side - Colorway Image (1/3 width) */}
                      <div className="col-span-1">
                        {designData.digital_design_colorway_image ? (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-500">COLORWAY IMAGE</p>
                            <div className="flex items-start">
                              <img 
                                src={designData.digital_design_colorway_image} 
                                alt="Colorway" 
                                className="w-auto h-[120px] object-contain rounded"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-400">COLORWAY IMAGE</p>
                            <div className="flex items-start">
                              <p className="text-xs text-gray-400">No image</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Points of Measurement */}
              {designData.poms && designData.poms.length > 0 && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">POINTS OF MEASUREMENT</p>
                  <div className="space-y-2">
                    {designData.poms.map((pom, index) => (
                      <div key={index} className="flex gap-4 items-center p-3 border border-gray-300">
                        <span className="text-sm font-bold w-20">SIZE {pom.size}</span>
                        <span className="flex-1 text-sm">{pom.point_name}</span>
                        <span className="text-sm font-bold">{pom.measurement} {pom.uom}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trims & Accessories */}
              {designData.trims && designData.trims.length > 0 && (
                <div className="p-5 border-2 border-black">
                  <p className="text-xs font-semibold tracking-wider text-gray-500 mb-4">TRIMS & ACCESSORIES</p>
                  <div className="space-y-2">
                    {designData.trims.map((trim, index) => {
                      // Determine master type based on code prefix
                      const masterType = trim.trim_code.startsWith('LN-Z') ? 'Trim' : 'Packaging';
                      
                      return (
                        <div key={index} className="flex gap-4 items-center p-3 border border-gray-300">
                          <span 
                            className="text-sm font-bold w-32 underline cursor-pointer hover:text-blue-600"
                            onClick={() => handleMasterDataClick(trim.trim_code, masterType)}
                          >
                            {trim.trim_code}
                          </span>
                          <span className="flex-1 text-sm">{trim.color_code} - {trim.color_name}</span>
                          <span className="text-sm font-bold">QTY {trim.quantity}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
        ) : null}
      </main>

      {/* Master Data Details Popup */}
      <Dialog open={masterDataOpen} onOpenChange={setMasterDataOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{masterDataCode || 'Master Data Details'}</DialogTitle>
          </DialogHeader>
          {masterDataLoading ? (
            <div className="py-8 text-center">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : masterDataDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-xs font-semibold text-gray-500">CODE</p>
                  <p className="text-sm font-bold">{masterDataDetails.ln_code || masterDataDetails.ln_z_code || masterDataDetails.ln_y_code || masterDataDetails.ln_t_code}</p>
                </div>
                
                {/* Show PRICE for all codes (LN, LN-Z, LN-Y, LN-T) */}
                {(masterDataDetails.colors && masterDataDetails.colors.length > 0) || masterDataDetails.price_per_meter ? (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">PRICE</p>
                    <p className="text-sm font-bold">
                      {masterDataDetails.price_per_meter 
                        ? `$${Number(masterDataDetails.price_per_meter).toFixed(2)}/Meter`
                        : masterDataDetails.colors && masterDataDetails.colors[0]?.price_per_meter 
                        ? `$${Number(masterDataDetails.colors[0].price_per_meter).toFixed(2)}/Meter`
                        : masterDataDetails.colors && masterDataDetails.colors[0]?.price 
                        ? `$${Number(masterDataDetails.colors[0].price).toFixed(2)}`
                        : '-'}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-gray-500">No data found</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Costing Dialog */}
      <Dialog open={showCostingDialog} onOpenChange={setShowCostingDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase border-b-2 border-black pb-2">
              PRE-PRODUCTION COSTING - {designData?.article_code}
            </DialogTitle>
          </DialogHeader>
          {designData?.costing_data && (
            <div className="space-y-4 mt-4">
              {/* Financial Document Style */}
              <div className="bg-white border-4 border-black">
                <div className="bg-black text-white p-4">
                  <h3 className="text-lg font-bold uppercase tracking-wider">PRE-PRODUCTION COSTING STATEMENT</h3>
                </div>
                
                <div className="p-6">
                  {/* Cost Breakdown Table */}
                  <table className="w-full border-2 border-black">
                    <thead>
                      <tr className="border-b-2 border-black bg-gray-100">
                        <th className="text-left p-4 border-r-2 border-black font-bold uppercase text-sm">COST CATEGORY</th>
                        <th className="text-right p-4 font-bold uppercase text-sm">AMOUNT (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-black">
                        <td className="p-4 border-r-2 border-black font-medium">Fabrics Total</td>
                        <td className="p-4 text-right font-mono">${designData.costing_data.fabricsTotal?.toFixed(2) || '0.00'}</td>
                      </tr>
                      {designData.costing_data.digitalDesignTotal > 0 && (
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Digital Design Total</td>
                          <td className="p-4 text-right font-mono">${designData.costing_data.digitalDesignTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                      )}
                      <tr className="border-b border-black">
                        <td className="p-4 border-r-2 border-black font-medium">Trims & Accessories Total</td>
                        <td className="p-4 text-right font-mono">${designData.costing_data.trimsTotal?.toFixed(2) || '0.00'}</td>
                      </tr>
                      <tr className="border-b border-black">
                        <td className="p-4 border-r-2 border-black font-medium">CMT (Cut, Make, Trim) Total</td>
                        <td className="p-4 text-right font-mono">${designData.costing_data.cmtTotal?.toFixed(2) || '0.00'}</td>
                      </tr>
                      {designData.costing_data.otherCostsTotal > 0 && (
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Other Costs Total</td>
                          <td className="p-4 text-right font-mono">${designData.costing_data.otherCostsTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                      )}
                      <tr className="border-t-4 border-black bg-gray-100">
                        <td className="p-4 border-r-2 border-black font-bold uppercase">SUBTOTAL (PRE-PRODUCTION COSTING)</td>
                        <td className="p-4 text-right font-bold font-mono">${designData.costing_data.preProductionCosting?.toFixed(2) || '0.00'}</td>
                      </tr>
                      <tr className="border-t-2 border-black">
                        <td className="p-4 border-r-2 border-black font-medium">Margin of Error (15%)</td>
                        <td className="p-4 text-right font-mono">${((designData.costing_data.preProductionCosting || 0) * 0.15).toFixed(2)}</td>
                      </tr>
                      <tr className="border-t-4 border-double border-black bg-black text-white">
                        <td className="p-4 border-r-2 border-white font-bold uppercase">TOTAL WITH MARGIN</td>
                        <td className="p-4 text-right font-bold font-mono">${designData.costing_data.withMargin?.toFixed(2) || '0.00'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tech Pack PDF Viewer */}
      <Dialog open={showTechPackPdf} onOpenChange={setShowTechPackPdf}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0" style={{ width: '95vw', height: '95vh' }}>
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Tech Pack PDF - {designData?.article_code}</DialogTitle>
          </DialogHeader>
          {designData?.tech_pack_pdf && (
            <div style={{ width: '100%', height: 'calc(95vh - 60px)', overflow: 'auto' }}>
              <iframe
                src={designData.tech_pack_pdf}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Tech Pack PDF"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Authorization Dialog */}
      <AuthorizationDialog
        open={authDialogOpen}
        onOpenChange={setAuthDialogOpen}
        onAuthorized={handleAuthSuccess}
        title={authAction === 'costing' ? 'View Costing' : 'View Tech Pack'}
        message={`Enter authorization code to view ${authAction === 'costing' ? 'costing information' : 'tech pack'}`}
        correctCode="override"
      />
    </div>
  );
}
